import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  transactionsQuery, accountsQuery, incassiQuery, usciteQuery, plurale, SOURCE_LABEL, SOURCE_TO_MATCH,
  type BankTransaction, type MatchKind, type UscitaSource,
} from './adminApi';
import { useAdminActions } from './useAdminActions';

/**
 * Movimenti — l'estratto conto Qonto.
 *   filterbar → conto · periodo · Entrate/Uscite · Solo non abbinati · cerca
 *   tabella   → Data · Descrizione · Conto · Importo · Abbinato a · Abbina/Scollega
 * Abbinare = registrare: l'API segna la voce incassata/pagata alla data valuta.
 * Le ricorrenti non hanno ancora una voce: si paga l'occorrenza con il movimento.
 * Stato in URL: acc · from · to · side · unmatched · q
 */
const PAGE_SIZE = 25;

export function MovimentiTab({ canWrite, showHistory }: { canWrite: boolean; showHistory?: boolean }) {
  const [accountId, setAccountId] = useUrlState<string>('acc', '');
  const [from, setFrom] = useUrlState<string>('from', '');
  const [to, setTo] = useUrlState<string>('to', '');
  const [sideParam, setSide] = useUrlState<string>('side', '');
  const side = sideParam === 'DEBIT' || sideParam === 'CREDIT' ? sideParam : '';
  const [unmatchedParam, setUnmatched] = useUrlState<string>('unmatched', '');
  const unmatched = unmatchedParam === '1';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [accountId, from, to, side, unmatched, search, showHistory]);

  // I saldi dei conti restano quelli veri: la data di taglio nasconde solo le righe.
  const { data: accounts } = useQuery(accountsQuery);
  const params = useMemo(() => ({
    accountId: accountId || undefined, from: from || undefined, to: to || undefined,
    side: side as 'DEBIT' | 'CREDIT' | '', unmatched, search: search.trim() || undefined,
    includeHistory: showHistory, page, pageSize: PAGE_SIZE,
  }), [accountId, from, to, side, unmatched, search, showHistory, page]);
  const q = useQuery(transactionsQuery(params));
  const items = q.data?.items ?? [];
  const qontoAccounts = (accounts ?? []).filter((a) => a.provider === 'QONTO');

  return (
    <>
      {/* Sei controlli: su 390 diventavano 218px di barra, la lista partiva
          sotto la piega. Dietro un tasto solo (mobile.md §5). */}
      <MobileFilters attivi={[accountId, from, to, side, unmatched ? '1' : '', search.trim()].filter(Boolean).length}>
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        {qontoAccounts.length > 1 && (
          <select className="inp inp-sm" style={{ width: 160 }} value={accountId} onChange={(e) => setAccountId(e.target.value)} aria-label="Conto">
            <option value="">Tutti i conti</option>
            {qontoAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        <input className="inp inp-sm mono" type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Dal" style={{ width: 140 }} />
        <span className="mute text-[12px]">→</span>
        <input className="inp inp-sm mono" type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Al" style={{ width: 140 }} />
        <div className="divider" />
        <div className="flex items-center gap-1.5" role="group" aria-label="Verso">
          {([['', 'Tutti'], ['CREDIT', 'Entrate'], ['DEBIT', 'Uscite']] as const).map(([k, l]) => (
            <button key={k} onClick={() => setSide(k)} aria-pressed={side === k} className={side === k ? 'filter-pill is-active' : 'filter-pill'}>{l}</button>
          ))}
        </div>
        <button onClick={() => setUnmatched(unmatched ? '' : '1')} aria-pressed={unmatched} className={unmatched ? 'filter-pill is-active' : 'filter-pill'}>Solo non abbinati</button>
        <div className="tb-search flex-1" style={{ minWidth: 180, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca movimenti" placeholder="Cerca descrizione o controparte…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <span className="mute text-[12px]">{plurale(q.data?.total ?? 0, 'movimento', 'movimenti')}</span>
      </div>
      </MobileFilters>

      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={8} cols={6} withAvatar={false} /></tbody></table></div>
      ) : items.length === 0 ? (
        <EmptyState brand title="Nessun movimento" description={unmatched ? 'Tutti i movimenti di questo periodo sono abbinati.' : 'Collega Qonto e premi Aggiorna: i movimenti arrivano da soli.'} />
      ) : (
        <div className="ed-section">
          {/* Scheda sul telefono; la riga non apre niente, il gesto è "Abbina". */}
          <table className="tbl tbl--ed tbl--card tbl--static">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Data</th>
                <th>Descrizione</th>
                {/* 150 e non 130: "Conto principale" a 130 andava a capo in
                    OGNI riga, e una colonna a due righe sposta l'occhio a ogni
                    riga della tabella. 150 è una larghezza che l'app usa già. */}
                {qontoAccounts.length > 1 && <th style={{ width: 150 }}>Conto</th>}
                <th className="num" style={{ width: 130 }}>Importo</th>
                <th style={{ width: 220 }}>Abbinato a</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((t) => <Row key={t.id} t={t} canWrite={canWrite} showAccount={qontoAccounts.length > 1} />)}
            </tbody>
          </table>
          {q.data && <Pagination page={page} pageSize={q.data.pageSize} total={q.data.total} totalPages={q.data.totalPages} onPage={setPage} />}
        </div>
      )}
    </>
  );
}

function Row({ t, canWrite, showAccount }: { t: BankTransaction; canWrite: boolean; showAccount: boolean }) {
  const { unmatch } = useAdminActions();
  const [open, setOpen] = useState(false);
  const credit = t.side === 'CREDIT';
  const accountName = typeof t.account === 'string' ? t.account : t.account?.name ?? '—';
  const label = `${fmtDate(t.settledAt)} · € ${fmtEur(t.amount, true)}`;
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="key" data-l="Data" className="mono mute text-[13px]" style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.settledAt)}</td>
      <td data-m="hd" className="adm-clip">
        <div className="font-semibold" title={t.counterparty || t.label}>{t.counterparty || t.label}</div>
        {t.counterparty && t.label !== t.counterparty && <div className="text-[12px] mute" title={t.label}>{t.label}</div>}
      </td>
      {/* Il conto è lo stesso su quasi tutte le righe: in scheda è rumore. */}
      {showAccount && <td data-m="off" className="mute text-[13px]">{accountName}</td>}
      <td data-m="key" data-l="Importo" className={`num mono font-semibold ${credit ? '' : 'mute'}`} style={{ whiteSpace: 'nowrap' }}>{credit ? '+' : '−'} € {fmtEur(t.amount, true)}</td>
      <td data-m="key" data-l="Abbinato a">
        {t.matched
          ? <Chip tone="ok" dot>{t.matched.label}</Chip>
          : <span className="mute-2 text-[13px]">—</span>}
      </td>
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1 relative">
          {canWrite && (t.matched ? (
            <button className="btn btn-sm btn-ghost" disabled={unmatch.isPending} title="Togli l'abbinamento" onClick={() => unmatch.mutate({ transactionId: t.id, label })}>
              <Icon name="unlink" size={10} /> Scollega
            </button>
          ) : (
            <button className="btn btn-sm" title="Abbina a una rata o a un'uscita" onClick={() => setOpen((v) => !v)}>
              <Icon name="link" size={10} /> Abbina
            </button>
          ))}
          {open && <MatchPopover t={t} onClose={() => setOpen(false)} />}
        </div>
      </td>
    </tr>
  );
}

interface Candidate { key: string; kind: MatchKind | null; source?: UscitaSource; id: string; label: string; amount: number; dueDate: string | null; exact: boolean }

/** Popover "Abbina": entrate → rate aperte; uscite → uscite aperte. Prima i candidati con lo stesso importo. */
function MatchPopover({ t, onClose }: { t: BankTransaction; onClose: () => void }) {
  const { match, pay } = useAdminActions();
  const credit = t.side === 'CREDIT';
  const [choice, setChoice] = useState('');
  const [search, setSearch] = useState('');
  const { data: incassi } = useQuery({ ...incassiQuery({ status: 'aperte', search: search.trim() || undefined, pageSize: 50 }), enabled: credit });
  const { data: uscite } = useQuery({ ...usciteQuery({ status: 'aperte', search: search.trim() || undefined, pageSize: 50 }), enabled: !credit });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const options = useMemo<Candidate[]>(() => {
    const same = (a: number) => Math.abs(a - t.amount) < 0.005;
    if (credit) {
      return (incassi?.items ?? [])
        .map<Candidate>((r) => ({ key: `INVOICE_PAYMENT:${r.id}`, kind: 'INVOICE_PAYMENT', id: r.id, label: `${r.invoiceNumber} · ${r.client.shortName}${r.ofTotal > 1 ? ` · rata ${r.seq}/${r.ofTotal}` : ''}`, amount: r.amount, dueDate: r.dueDate, exact: same(r.amount) }))
        .sort((a, b) => Number(b.exact) - Number(a.exact));
    }
    return (uscite?.items ?? [])
      .map<Candidate>((r) => ({ key: `${r.source}:${r.id}:${r.dueDate ?? ''}`, kind: SOURCE_TO_MATCH[r.source], source: r.source, id: r.id, label: `${SOURCE_LABEL[r.source]} · ${r.counterparty}${r.description ? ` · ${r.description}` : ''}${r.source === 'RECURRING' && r.dueDate ? ` · ${fmtDate(r.dueDate)}` : ''}`, amount: r.amount, dueDate: r.dueDate, exact: same(r.amount) }))
      .sort((a, b) => Number(b.exact) - Number(a.exact));
  }, [credit, incassi, uscite, t.amount]);

  const chosen = options.find((o) => o.key === choice);
  const exactCount = options.filter((o) => o.exact).length;

  const confirm = () => {
    if (!chosen) return;
    onClose();
    if (chosen.kind) {
      match.mutate({ transactionId: t.id, kind: chosen.kind, id: chosen.id, label: chosen.label, markPaid: true });
    } else {
      // Ricorrente: l'occorrenza nasce pagata con questo movimento (data valuta, metodo bonifico)
      pay.mutate({ source: 'RECURRING', id: chosen.id, amount: chosen.amount, label: chosen.label, dueDate: chosen.dueDate, paidDate: t.settledAt, method: 'BONIFICO', bankTransactionId: t.id });
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div className="dropdown adm-pop right-0 mt-1 z-[61]" role="dialog" aria-label="Abbina movimento">
        <div className="adm-pop-hd">
          <span className="t-micro">Abbina a</span>
          <span className="mono font-semibold">{credit ? '+' : '−'} € {fmtEur(t.amount, true)}</span>
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">Cerca</label>
          <input className="inp inp-sm" placeholder={credit ? 'Fattura o cliente' : 'Fornitore o descrizione'} value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          <label className="field-label">{credit ? 'Rata' : 'Uscita'}</label>
          <select className="inp inp-sm" value={choice} onChange={(e) => setChoice(e.target.value)} size={Math.min(6, Math.max(2, options.length + 1))}>
            <option value="">{options.length ? `${options.length} candidati${exactCount ? ` · ${exactCount} stesso importo` : ''}` : 'Nessuna voce aperta'}</option>
            {options.map((o) => (
              <option key={o.key} value={o.key}>{o.exact ? '● ' : ''}€ {fmtEur(o.amount, true)} · {o.label}</option>
            ))}
          </select>
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm btn-primary" disabled={!chosen || match.isPending || pay.isPending} onClick={confirm}>
            <Icon name="link" size={10} /> Abbina
          </button>
        </div>
      </div>
    </>
  );
}
